import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import '../styles/WorkHistory.css';

gsap.registerPlugin(ScrollTrigger);

const jobs = [
  {
    id: 'job-1',
    role: 'Senior ERPNext Developer',
    company: 'Codx Pro',
    type: 'Full-time · Remote',
    period: '05/2025 — PRESENT',
    location: 'Remote',
    link: 'https://justyol.com',
    linkText: 'justyol.com',
    description:
      'Driving ERPNext enterprise integrations, Turkish Tax Authority compliance, and automated e-commerce synchronization engines for international clients like Justyol.',
    achievements: [
      'Architected end-to-end Shopify ↔ ERPNext real-time synchronization pipelines for Turkish e-commerce platform Justyol.',
      'Integrated ERPNext with Turkish Tax Authority e-invoicing systems and Meta WhatsApp API for automated customer order notifications.',
      'Refactored legacy codebases to cut technical debt, resolving critical bugs and boosting system stability.',
      'Created custom financial reports and Doctypes for automated PDF and XML data ingestion.',
      'Developed custom inventory and production workflows tailored for meat processing operations.'
    ],
    techStack: ['ERPNext', 'Frappe', 'Python', 'Shopify API', 'WhatsApp API', 'Turkish Tax E-Invoicing']
  },
  {
    id: 'job-2',
    role: 'ERPNext Developer',
    company: 'AMK LLC',
    type: 'Full-time · On-site',
    period: '02/2025 — 10/2025',
    location: 'Cairo, Egypt',
    description:
      'Engineered core Frappe customizations, automated HR background engines, and backend microservices powering on-demand shared transportation apps.',
    achievements: [
      'Upgraded the Frappe HR module by extending core Doctypes and deploying automated background jobs for employee attendance tracking.',
      'Created custom ERPNext applications integrating webhooks for automated invoice generation and Firebase Cloud Messaging for push notifications.',
      'Built high-performance RESTful APIs handling core CRUD operations, collaborating closely with Flutter, React, and Nest.js teams.',
      'Enforced software design patterns, conducted code reviews, and managed sprints via Jira and Confluence.'
    ],
    techStack: ['ERPNext', 'Frappe HR', 'Python', 'Firebase', 'REST APIs', 'Jira / Confluence']
  },
  {
    id: 'job-3',
    role: 'Full Stack Developer',
    company: 'Triple Gates',
    type: 'Full-time · Remote',
    period: '01/2025 — 02/2025',
    location: 'Remote',
    description:
      'Led the UI/UX redesign of the Frappe platform, creating custom modular apps to enhance interface aesthetics, usability, and workflow ergonomics.',
    achievements: [
      'Developed a custom Frappe application from scratch to revamp the platform’s visual identity and user interface.',
      'Extended Frappe UI components using modern JavaScript and CSS to deliver seamless responsive workflows.',
      'Collaborated in an Agile development environment leveraging Microsoft Azure DevOps for backlog and sprint management.'
    ],
    techStack: ['Frappe Framework', 'JavaScript', 'CSS3', 'UI/UX Design', 'Azure DevOps']
  },
  {
    id: 'job-4',
    role: 'Python Developer',
    company: 'Appy Innovate',
    type: 'Full-time · Remote',
    period: '05/2024 — 01/2025',
    location: 'Remote',
    description:
      'Led backend development for sales management solutions and implemented Saudi Arabia’s ZATCA Phase 2 e-invoicing compliance on ERPNext.',
    achievements: [
      'Accelerated customer migration timelines by 10x through systematic customizations refactoring and pipeline optimization.',
      'Hardened ZATCA transmission functions for bulk invoice testing, automated validation, and error recovery.',
      'Built custom Doctypes and print formats, seamlessly integrating sales invoices with external API endpoints.',
      'Containerized the application with Docker and orchestrated deployment onto Linux production servers, mentoring junior team members on SOLID design.'
    ],
    techStack: ['Python', 'Frappe', 'ERPNext', 'ZATCA Phase 2', 'Docker', 'Linux']
  }
];

const WorkHistory = () => {
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const timelineRef = useRef(null);
  const progressBarRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Header entrance animation
      gsap.from(titleRef.current, {
        y: 50,
        autoAlpha: 0,
        duration: 0.9,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 80%',
          toggleActions: 'play none none none'
        },
        clearProps: 'transform,opacity,visibility'
      });

      // Spine progress line animation (Apple-style scrollytelling fill)
      if (timelineRef.current && progressBarRef.current) {
        gsap.fromTo(
          progressBarRef.current,
          { height: '0%' },
          {
            height: '100%',
            ease: 'none',
            scrollTrigger: {
              trigger: timelineRef.current,
              start: 'top 65%',
              end: 'bottom 60%',
              scrub: 0.5
            }
          }
        );
      }

      // Individual timeline items activation on scroll
      const items = timelineRef.current?.querySelectorAll('.timeline-item');
      if (items) {
        items.forEach((item) => {
          ScrollTrigger.create({
            trigger: item,
            start: 'top 65%',
            end: 'bottom 35%',
            toggleClass: { targets: item, className: 'is-active' },
            toggleActions: 'play reverse play reverse'
          });
        });
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="work-history" id="work-history">
      <div className="work-history-container">
        {/* Header */}
        <div ref={titleRef} className="work-history-header">
          <span className="work-history-label">Career Journey</span>
          <h2 className="work-history-title">Work History</h2>
          <p className="work-history-subtitle">
            A chronological timeline of my professional roles, engineering milestones, and impact delivered.
          </p>
        </div>

        {/* Timeline Container */}
        <div ref={timelineRef} className="timeline-container">
          {/* Vertical spine & progress fill */}
          <div className="timeline-spine">
            <div ref={progressBarRef} className="timeline-spine-progress" />
          </div>

          {/* Timeline Items */}
          <div className="timeline-items">
            {jobs.map((job) => (
              <div key={job.id} className="timeline-item">
                {/* Node Marker on Spine */}
                <div className="timeline-marker" aria-hidden="true">
                  <div className="timeline-node">
                    <div className="timeline-node-inner" />
                  </div>
                  <div className="timeline-marker-pulse" />
                </div>

                {/* Content Card */}
                <div className="timeline-card">
                  {/* Card Header */}
                  <div className="timeline-card-header">
                    <div className="timeline-role-row">
                      <h3 className="timeline-role">{job.role}</h3>
                      <div className="timeline-period-badge">{job.period}</div>
                    </div>
                    <div className="timeline-company-row">
                      <span className="timeline-company">{job.company}</span>
                      <span className="timeline-dot">•</span>
                      <span className="timeline-type">{job.type}</span>
                      <span className="timeline-dot">•</span>
                      <span className="timeline-location">{job.location}</span>
                      {job.link && (
                        <>
                          <span className="timeline-dot">•</span>
                          <a
                            href={job.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="timeline-job-link"
                          >
                            {job.linkText || 'Visit Website'} ↗
                          </a>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <p className="timeline-description">{job.description}</p>

                  {/* Key Achievements */}
                  <div className="timeline-achievements">
                    <span className="timeline-achievements-title">Key Contributions:</span>
                    <ul className="timeline-bullets">
                      {job.achievements.map((ach, i) => (
                        <li key={i} className="timeline-bullet-item">
                          <span className="timeline-bullet-icon">▹</span>
                          <span className="timeline-bullet-text">{ach}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Tech Stack Tags */}
                  <div className="timeline-tech-tags">
                    {job.techStack.map((tech, i) => (
                      <span key={i} className="timeline-tech-badge">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WorkHistory;
